/* Shared chapter dock: the chapter crossing the viewport midpoint owns the
   active pill and its progress underline, just as on the homepage. */
const initialized = new WeakMap();

export function initializeChapterDocks(root = document) {
  return [...root.querySelectorAll("[data-dock]")].map((dock) => {
    if (initialized.has(dock)) return initialized.get(dock);
    const doc = dock.ownerDocument;
    const win = doc.defaultView;
    const chapters = [...dock.querySelectorAll("[data-dock-link]")].flatMap((link) => {
      const href = link.getAttribute("href");
      if (!href?.startsWith("#")) return [];
      let id;
      try { id = decodeURIComponent(href.slice(1)); } catch { return []; }
      const el = doc.getElementById(id);
      return el ? [{ el, link }] : [];
    });
    let ticking = false;
    let frame = 0;
    let activeLink = null;
    let width = 0;

    // Move only this horizontal rail; scrollIntoView would also move the page.
    function reveal(link) {
      const rail = dock.getBoundingClientRect();
      const pill = link.getBoundingClientRect();
      const inset = 5;
      if (pill.left < rail.left + inset) dock.scrollLeft += pill.left - rail.left - inset;
      else if (pill.right > rail.right - inset) dock.scrollLeft += pill.right - rail.right + inset;
    }

    function update() {
      ticking = false;
      const line = win.innerHeight * 0.5;
      let current = null;
      for (const { el, link } of chapters) {
        const r = el.getBoundingClientRect();
        const available = !dock.hidden && !el.closest("[hidden]") && r.height > 0;
        const active = available && r.top <= line && r.bottom > line;
        const progress = available ? Math.min(1, Math.max(0, (line - r.top) / r.height)) : 0;
        link.style.setProperty("--p", progress.toFixed(3));
        if (active) { link.setAttribute("aria-current", "step"); current = link; }
        else link.removeAttribute("aria-current");
      }
      const visible = !!current;
      dock.style.setProperty("--show", visible ? "1" : "0");
      dock.dataset.dockVisible = String(visible);
      dock.inert = !visible;
      if (visible) dock.removeAttribute("aria-hidden");
      else dock.setAttribute("aria-hidden", "true");
      if (current && (current !== activeLink || dock.clientWidth !== width)) reveal(current);
      activeLink = current;
      width = dock.clientWidth;
    }

    function schedule() {
      if (!ticking) { ticking = true; frame = win.requestAnimationFrame(update); }
    }
    const onFocus = (event) => {
      const link = event.target.closest("[data-dock-link]");
      if (link && !dock.inert) reveal(link);
    };
    win.addEventListener("scroll", schedule, { passive: true });
    win.addEventListener("resize", schedule);
    win.addEventListener("hashchange", schedule);
    dock.addEventListener("focusin", onFocus);
    // The homepage router owns hidden; react when it switches to the directory.
    const observer = new win.MutationObserver(update);
    observer.observe(dock, { attributes: true, attributeFilter: ["hidden"] });
    doc.fonts?.ready.then(schedule);
    update();
    const controller = {
      update,
      destroy() {
        win.cancelAnimationFrame(frame);
        win.removeEventListener("scroll", schedule);
        win.removeEventListener("resize", schedule);
        win.removeEventListener("hashchange", schedule);
        dock.removeEventListener("focusin", onFocus);
        observer.disconnect();
        initialized.delete(dock);
      },
    };
    initialized.set(dock, controller);
    return controller;
  });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => initializeChapterDocks(), { once: true });
} else initializeChapterDocks();
